import { isNull } from 'lodash';
import type { CompanyDocuments, DocumentReferencesView, FolderView, MeasurementDateName } from 'api';
import { orderDocumentFoldersForDisplay } from 'utilities/documentFolderSorting';

type ProcessedDocumentReference = DocumentReferencesView & {
  uploaded_date?: string;
};

type ProcessedFolder = FolderView & {
  documentId?: number;
  files: ProcessedDocumentReference[];
  measurementDate: MeasurementDateName;
  subFolders: ProcessedFolder[];
};

/*
processDocumentsInformation is a function that allows you to organize the documents
with their respective folders, in the corresponding measurement date.
*/

const getFolderTree = (folders: ProcessedFolder[]) => {
  const map: Record<number, number> = {};
  const folderList: ProcessedFolder[] = [];

  folders.forEach((folder, idx) => {
    if (folder.id !== undefined) {
      map[folder.id] = idx;
    }
  });

  folders.forEach(folder => {
    const parentFolderId = folder.parent_folder?.id;
    const parentFolderIndex = parentFolderId ? map[parentFolderId] : undefined;

    if (parentFolderIndex !== undefined) {
      folders[parentFolderIndex].subFolders.push(folder);
    } else {
      folderList.push(folder);
    }
  });
  return folderList;
};

const orderFolderTreeForDisplay = (folders: ProcessedFolder[]): ProcessedFolder[] =>
  orderDocumentFoldersForDisplay(
    folders.map(folder => ({
      ...folder,
      subFolders: orderFolderTreeForDisplay(folder.subFolders ?? []),
    }))
  );

const getFoldersWithFiles = (folders: ProcessedFolder[], files: DocumentReferencesView[]) => {
  files.forEach(fileItem => {
    const { file, folder: folderId } = fileItem;
    const newFileObj = {
      ...fileItem,
      uploaded_date: file.uploaded_date,
    };
    if (!isNull(folderId)) {
      const folderIdx = folders.findIndex(folder => folder.id === folderId);
      if (folderIdx !== -1) {
        folders[folderIdx].files.push(newFileObj);
      }
    }
  });
  return folders;
};

const addInformationToFolders = (
  id: number | undefined,
  measurementDate: MeasurementDateName,
  folders: FolderView[]
): ProcessedFolder[] =>
  folders.map(folder => ({
    ...folder,
    documentId: id,
    measurementDate,
    files: [],
    subFolders: [],
  }));

const getFoldersData = (foundDocuments: CompanyDocuments) => {
  const { id, measurement_date: measurementDate, folders, files } = foundDocuments;

  const foldersWithInfo = addInformationToFolders(id, measurementDate, folders ?? []);
  const foldersWithFiles = getFoldersWithFiles(foldersWithInfo, files ?? []);
  const folderTree = getFolderTree(foldersWithFiles);
  return orderFolderTreeForDisplay(folderTree);
};

const getFilesWithoutFolders = (files: DocumentReferencesView[]) => {
  const dataFiles = files.filter(itemFile => isNull(itemFile.folder));
  return dataFiles.map(fileItem => ({
    ...fileItem,
    uploaded_date: fileItem.file.uploaded_date,
  }));
};

export const processDocumentsInformation = (documentsObject: CompanyDocuments) => ({
  documentId: documentsObject.id,
  filesWithoutFolders: getFilesWithoutFolders(documentsObject.files ?? []),
  folders: getFoldersData(documentsObject),
  measurementDate: documentsObject.measurement_date,
});
